import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import Fuse from 'fuse.js';
import debounce from 'lodash/debounce';
import PropTypes from 'prop-types';

import { useFetch } from 'hooks';

// Components
import { Mui } from 'components';

import * as api from './api';

const MIN_SEARCH_LENGTH = 3;
const DEBOUNCE_DELAY = 300;
const API_RESULTS_LIMIT = 10;

function getSearchResultHref(result, sellerSlug) {
  const typeMap = {
    ticket: `/sellers/${sellerSlug}/support/tickets/${result.reference}`,
    order: `/sellers/${sellerSlug}/sales/${result.reference}`,
  };
  return typeMap[result.type] || null;
}

function getSearchResultIcon(type) {
  const iconMap = {
    ticket: 'support_agent',
    order: 'shopping_cart',
  };
  return iconMap[type] || 'search';
}

const useStyles = Mui.makeStyles(theme => ({
  dialogPaper: {
    borderRadius: 12,
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    overflow: 'hidden',
    backgroundColor: theme.palette.background.paper,
    marginTop: '10vh',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '60vh',
    [theme.breakpoints.down('xs')]: {
      margin: theme.spacing(2),
      width: `calc(100% - ${theme.spacing(4)}px)`,
    },
  },
  dialogScrollPaper: {
    alignItems: 'flex-start',
  },
  backdrop: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(4px)',
  },
  searchContainer: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  commandHint: {
    backgroundColor: theme.palette.action.hover,
    borderRadius: 4,
    padding: theme.spacing(0.5, 1),
    fontSize: '0.75rem',
    fontWeight: 'bold',
    color: theme.palette.text.secondary,
  },
  groupHeader: {
    lineHeight: '32px',
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    '& > span': {
      fontSize: '0.8rem',
      textTransform: 'uppercase',
      fontWeight: 600,
    },
    '& .MuiChip-root': {
      fontWeight: 400,
    },
  },
  listItem: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    margin: theme.spacing(0, 1),
    borderRadius: 8,
    width: `calc(100% - ${theme.spacing(2)}px)`,
    '&.Mui-selected': {
      backgroundColor: theme.palette.action.selected,
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
    },
  },
  listItemIcon: {
    minWidth: 40,
    color: theme.palette.text.secondary,
  },
}));

function removeAccents(str) {
  return str ? str.normalize('NFD').replace(/[\u0300-\u036f]/g, '') : '';
}

function flattenItems(items = [], parentTitle = '', parentGroup = {}) {
  return items.reduce((acc, item) => {
    if (item.invisible || item.disabled) return acc;
    const fullTitle = parentTitle ? `${parentTitle} › ${item.title}` : item.title;

    if (item.items && item.items.length > 0) {
      return [...acc, ...flattenItems(item.items, fullTitle, parentGroup)];
    }

    if (item.href) {
      return [
        ...acc,
        {
          ...item,
          parentGroup,
          fullTitle,
          searchableText: removeAccents(
            `${parentGroup.subheader || ''} ${fullTitle}`.toLowerCase(),
          ),
        },
      ];
    }
    return acc;
  }, []);
}

function getIconFor(item) {
  const Avatar = item.avatar;
  const Icon = item.icon;
  if (Avatar) return <Avatar style={{ width: 24, height: 24 }} />;
  if (Icon) return <Icon fontSize="small" />;
  return <Mui.Icon fontSize="small">chevron_right</Mui.Icon>;
}

function Menu({ items, isStaff }) {
  const classes = useStyles();
  const history = useHistory();
  const { sellerSlug } = useParams();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const listRef = useRef(null);

  const flatItems = useMemo(
    () =>
      (items || []).reduce((acc, group) => {
        if (group.invisible || group.disabled || (group.isStaff && !isStaff)) return acc;
        return [...acc, ...flattenItems(group.items, '', group)];
      }, []),
    [items, isStaff],
  );

  const fuse = useMemo(
    () =>
      new Fuse(flatItems, {
        keys: ['searchableText'],
        threshold: 0.2,
        ignoreLocation: true,
        minMatchCharLength: 1,
      }),
    [flatItems],
  );

  const filteredItems = useMemo(() => {
    if (!query) return flatItems;
    const normalizedQuery = removeAccents(query.toLowerCase());
    return fuse.search(normalizedQuery).map(result => result.item);
  }, [flatItems, query, fuse]);

  const groupedFilteredItems = useMemo(
    () =>
      filteredItems.reduce((acc, item, index) => {
        const sh = item.parentGroup?.subheader || 'Outros';
        const currentGroup = acc[sh] || { group: item.parentGroup, elements: [] };
        return {
          ...acc,
          [sh]: {
            ...currentGroup,
            elements: [...currentGroup.elements, { ...item, globalIndex: index }],
          },
        };
      }, {}),
    [filteredItems],
  );

  const isSearchValid = sellerSlug && debouncedQuery.length >= MIN_SEARCH_LENGTH;

  const { data, isValidating } = useFetch([
    isSearchValid ? api.endpoints.globalSearch(sellerSlug) : null,
    { q: debouncedQuery, limit: API_RESULTS_LIMIT },
  ]);

  const results = useMemo(() => data?.results || [], [data?.results]);

  const allNavigableItems = useMemo(() => {
    const menuItems = filteredItems.map(item => ({ ...item, source: 'menu' }));
    const searchItems = results.map(result => ({
      source: 'api',
      ...result,
      href: getSearchResultHref(result, sellerSlug),
    }));
    return [...menuItems, ...searchItems];
  }, [filteredItems, results, sellerSlug]);

  const updateDebouncedQuery = useMemo(
    () => debounce(value => setDebouncedQuery(value), DEBOUNCE_DELAY),
    [],
  );

  useEffect(() => () => updateDebouncedQuery.cancel(), [updateDebouncedQuery]);

  useEffect(() => {
    const handleKeyDown = e => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(prev => !prev);
      }
    };

    const handleCustomEvent = () => setOpen(true);

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('open-command-palette', handleCustomEvent);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('open-command-palette', handleCustomEvent);
    };
  }, []);

  useEffect(() => {
    if (open && listRef.current) {
      const selectedEl = listRef.current.querySelector('.Mui-selected');
      if (selectedEl) {
        selectedEl.scrollIntoView({ block: 'nearest', behavior: 'auto' });
      }
    }
  }, [selectedIndex, open]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleExited = () => {
    setQuery('');
    setSelectedIndex(0);
    setDebouncedQuery('');
  };

  const handleSelect = item => {
    if (item.target === '_blank') {
      window.open(item.href, '_blank');
    } else if (item.href) {
      history.push(item.href);
    }
    handleClose();
  };

  const handleQueryChange = value => {
    setQuery(value);
    setSelectedIndex(0);
    updateDebouncedQuery(value);
  };

  const handleDialogKeyDown = e => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev < allNavigableItems.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (allNavigableItems[selectedIndex]) {
          handleSelect(allNavigableItems[selectedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        handleClose();
        break;
      default:
        break;
    }
  };

  const renderListItem = (item, currentIndex) => {
    const isSelected = currentIndex === selectedIndex;

    return (
      <Mui.ListItem
        button
        key={`${item.href}-${currentIndex}`}
        className={classes.listItem}
        selected={isSelected}
        onClick={() => handleSelect(item)}
        onMouseEnter={() => setSelectedIndex(currentIndex)}>
        <Mui.ListItemIcon className={classes.listItemIcon}>
          {item.isStaff || item.isNew ? (
            <Mui.Badge
              color={item.isStaff ? 'error' : 'primary'}
              variant="dot"
              anchorOrigin={{ vertical: 'top', horizontal: 'left' }}>
              {getIconFor(item)}
            </Mui.Badge>
          ) : (
            getIconFor(item)
          )}
        </Mui.ListItemIcon>
        <Mui.ListItemText
          primary={item.fullTitle}
          primaryTypographyProps={{
            variant: 'body2',
            noWrap: true,
            color: 'textPrimary',
          }}
          style={{ margin: 0 }}
        />
        {item.info && (
          <Mui.Box ml={1} display="flex" alignItems="center">
            {item.info?.({})}
          </Mui.Box>
        )}
        {item.isNew && (
          <Mui.Box ml={1}>
            <Mui.Chip size="small" label="NOVO" color="primary" />
          </Mui.Box>
        )}
      </Mui.ListItem>
    );
  };

  const renderSearchResultItem = (result, currentIndex) => {
    const isSelected = currentIndex === selectedIndex;
    const href = getSearchResultHref(result, sellerSlug);

    return (
      <Mui.ListItem
        button
        key={result.uid}
        className={classes.listItem}
        selected={isSelected}
        disabled={!href}
        onClick={() => handleSelect({ href })}
        onMouseEnter={() => setSelectedIndex(currentIndex)}>
        <Mui.ListItemIcon className={classes.listItemIcon}>
          <Mui.Icon fontSize="small">{getSearchResultIcon(result.type)}</Mui.Icon>
        </Mui.ListItemIcon>
        <Mui.ListItemText
          primary={result.label}
          secondary={result.extra?.title || result.extra?.statusDisplay}
          primaryTypographyProps={{ variant: 'body2', noWrap: true }}
          secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
          style={{ margin: 0 }}
        />
        {result.extra?.statusDisplay && (
          <Mui.Box ml={1}>
            <Mui.Chip size="small" label={result.extra.statusDisplay} variant="outlined" />
          </Mui.Box>
        )}
      </Mui.ListItem>
    );
  };

  const showApiSection =
    sellerSlug &&
    debouncedQuery.length >= MIN_SEARCH_LENGTH &&
    (isValidating || results.length > 0);

  return (
    <Mui.Dialog
      open={open}
      onClose={handleClose}
      TransitionProps={{ onExited: handleExited }}
      fullWidth
      maxWidth="sm"
      classes={{ paper: classes.dialogPaper, scrollPaper: classes.dialogScrollPaper }}
      BackdropProps={{ classes: { root: classes.backdrop } }}
      onKeyDown={handleDialogKeyDown}>
      <div className={classes.searchContainer}>
        <Mui.Icon color="action">search</Mui.Icon>
        <Mui.InputBase
          autoFocus
          placeholder="O que você precisa acessar agora?"
          style={{ flex: 1, marginLeft: 16, fontSize: '1.2rem' }}
          value={query}
          onChange={e => handleQueryChange(e.target.value)}
        />
        <Mui.Typography
          variant="caption"
          className={classes.commandHint}
          style={{ cursor: 'pointer' }}
          onClick={handleClose}>
          ESC
        </Mui.Typography>
      </div>
      <Mui.Box flex={1} py={1} style={{ overflowY: 'auto' }} ref={listRef}>
        {filteredItems.length === 0 && !showApiSection && (
          <Mui.Box py={6} px={2} textAlign="center" color="text.secondary">
            <Mui.Typography variant="body1">
              Nenhum resultado encontrado para &quot;{query}&quot;
            </Mui.Typography>
          </Mui.Box>
        )}
        {filteredItems.length > 0 && query && (
          <Mui.List>{filteredItems.map((item, index) => renderListItem(item, index))}</Mui.List>
        )}
        {filteredItems.length > 0 &&
          !query &&
          Object.values(groupedFilteredItems).map(({ group, elements }) => {
            const groupName = group?.subheader || 'Outros';
            return (
              <Mui.List
                key={groupName}
                subheader={
                  <Mui.ListSubheader className={classes.groupHeader} disableSticky>
                    <span>{groupName}</span>
                    {group?.isStaff && (
                      <Mui.Chip
                        size="small"
                        label="STAFF"
                        style={{ backgroundColor: '#f44336', color: 'white' }}
                      />
                    )}
                    {!group?.isStaff && group?.isNew && (
                      <Mui.Chip size="small" label="NOVO" color="primary" />
                    )}
                  </Mui.ListSubheader>
                }>
                {elements.map(item => renderListItem(item, item.globalIndex))}
              </Mui.List>
            );
          })}
        {showApiSection && (
          <Mui.List
            subheader={
              <Mui.ListSubheader className={classes.groupHeader} disableSticky>
                <span>Mais Resultados</span>
                {isValidating && <Mui.CircularProgress size={16} />}
              </Mui.ListSubheader>
            }>
            {results.map((result, idx) => {
              const globalIndex = filteredItems.length + idx;
              return renderSearchResultItem(result, globalIndex);
            })}
          </Mui.List>
        )}
      </Mui.Box>
    </Mui.Dialog>
  );
}

Menu.propTypes = {
  items: PropTypes.array,
  isStaff: PropTypes.bool,
};

Menu.defaultProps = {
  items: [],
  isStaff: false,
};

export default Menu;
